import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    is_wholesale: false,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4" data-testid="contact-success">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-[#E5E0D8] text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-['Bodoni_Moda'] text-3xl font-bold text-[#2A2A2A] mb-2">
            Message Received!
          </h1>
          <p className="text-[#666] mb-6">
            Thanks for reaching out. We'll respond soon—usually within 24-48 hours.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '',
                email: '',
                subject: '',
                message: '',
                is_wholesale: false,
              });
            }}
            variant="outline"
            className="border-[#2A2A2A]"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="contact-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center mb-12">
          <h1 className="font-['Bodoni_Moda'] text-4xl sm:text-5xl font-bold text-[#2A2A2A] mb-4">
            Get in Touch
          </h1>
          <p className="text-[#666] max-w-xl mx-auto">
            Questions, feedback, or just want to say hey? We'd love to hear from you.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E0D8]">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1"
                  data-testid="contact-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="mt-1"
                data-testid="contact-subject"
              />
            </div>

            <div>
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className="mt-1 resize-none"
                data-testid="contact-message"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="wholesale"
                checked={formData.is_wholesale}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_wholesale: checked })
                }
                data-testid="contact-wholesale"
              />
              <label htmlFor="wholesale" className="text-sm text-[#666] cursor-pointer">
                This is a wholesale or retail inquiry
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4540A] hover:bg-[#B84709] text-white py-6 text-lg font-semibold"
              data-testid="contact-submit"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </div>

        {/* Wholesale Note */}
        <div className="mt-8 bg-[#2A2A2A] rounded-xl p-6 text-center">
          <Mail className="w-8 h-8 text-[#D4540A] mx-auto mb-3" />
          <h3 className="font-semibold text-white mb-2">Wholesale & Retail Partners</h3>
          <p className="text-[#B8B5B0] text-sm">
            Interested in carrying Country Bo in your store or restaurant? We'd love to chat. Check the wholesale box above or mention it in your message.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
